import React from 'react';
import styled from 'styled-components/macro';

import formatNumber from 'helpers/formatNumber';
import getNumberColor from 'helpers/getNumberColor';
import { ABBREVIATION_THRESHOLD } from 'helpers/abbreviateNumber';

import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import PositionArrow from 'components/utility/PositionArrow/PositionArrow';
import CustomerIcon, {
  StyledIcon,
} from 'components/base/CustomerIcon/CustomerIcon';
import { TableRow } from 'components/Overview/BreakdownTable/styles';
import { IPlaylist } from './types';

const CellWrapper = styled.div`
  display: flex;
  align-items: center;

  ${StyledIcon} {
    margin-right: ${(p): string => p.theme.spacing.small};
  }
`;

const NumberCell = styled.td<any>`
  text-align: right;
  color: ${({ theme, hasColor }): string =>
    hasColor ? theme[hasColor] : '#000a29'};
`;

const PositionWrapper = styled.span`
  display: inline-block;
  width: 4em;
  text-align: left;
  font-weight: 600;
`;

interface IPlaylistTableRow {
  playlist: IPlaylist;
  isLargeScreen: boolean;
}

const PlaylistTableRow: React.FC<IPlaylistTableRow> = (props) => {
  const { playlist, isLargeScreen } = props;

  const {
    displayName,
    position,
    previousPosition,
    stream_wow_in_progress: streamsWeekOverWeek,
    peak,
    skiprate_this_week_to_date: skiprate,
    followers,
    track_count: trackCount,
    customerId,
    stream_count_this_week_to_date: streams,
  } = playlist;

  const hasSWOWColor = getNumberColor(streamsWeekOverWeek);

  if (isLargeScreen) {
    return (
      <TableRow key={displayName}>
        <td>
          <CellWrapper>
            <CustomerIcon showHiddenPlaceholder customer={customerId} />{' '}
            {displayName}
          </CellWrapper>
        </td>
        <NumberCell>
          <PositionWrapper>
            {formatNumber(position, 'position')}
            <PositionArrow
              position={position}
              previousPosition={previousPosition}
              showTooltip={true}
            ></PositionArrow>
          </PositionWrapper>
        </NumberCell>
        <NumberCell>
          <TippyToolTip
            tooltipContent={formatNumber(streams, 'number', false)}
            canBeCopied
            enabled={!!streams && Math.abs(streams) >= ABBREVIATION_THRESHOLD}
          >
            {formatNumber(streams, 'number', true)}
          </TippyToolTip>
        </NumberCell>
        <NumberCell>{formatNumber(peak, 'number')}</NumberCell>
        <NumberCell hasColor={hasSWOWColor}>
          {formatNumber(streamsWeekOverWeek, 'trend')}
        </NumberCell>
        <NumberCell>{formatNumber(skiprate, 'trend')}</NumberCell>
        <NumberCell>
          <TippyToolTip
            tooltipContent={formatNumber(followers, 'number', false)}
            canBeCopied
            enabled={
              !!followers && Math.abs(followers) >= ABBREVIATION_THRESHOLD
            }
          >
            {formatNumber(followers, 'number', true)}
          </TippyToolTip>
        </NumberCell>
        <NumberCell>
          <TippyToolTip
            tooltipContent={formatNumber(trackCount, 'number', false)}
            canBeCopied
            enabled={
              !!trackCount && Math.abs(trackCount) >= ABBREVIATION_THRESHOLD
            }
          >
            {formatNumber(trackCount, 'number')}
          </TippyToolTip>
        </NumberCell>
      </TableRow>
    );
  }

  return (
    <TableRow key={displayName}>
      <td>
        <CellWrapper>
          <CustomerIcon showHiddenPlaceholder customer={customerId} />{' '}
          {displayName}
        </CellWrapper>
      </td>
      <NumberCell>
        <PositionWrapper>
          {formatNumber(position, 'position')}
          <PositionArrow
            position={position}
            previousPosition={previousPosition}
            showTooltip={true}
          ></PositionArrow>
        </PositionWrapper>
      </NumberCell>
    </TableRow>
  );
};

export default PlaylistTableRow;
